// the first thing we should load is the exception handler so we can catch all the errors
// importing this package has a side effect of initializing the error handler
import { setErrorHandler } from 'owa-exception-handler';

// must be require()'d here, or they will not be exposed by the expose loader
require('expose-loader?React!react');
require('expose-loader?ReactDOM!react-dom');

import * as React from 'react';
import asyncRender from './asyncRender';
import { ErrorBoundary } from 'owa-error-boundary';
import initializeFocusOutline from './initializeFocusOutline';
import initializeRouting from './initializeRouting';
import initializeSatchelMiddleware from './initializeSatchelMiddleware';
import initializeTracer from './initializeTracer';
import initializeWindowTitle from './initializeWindowTitle';
import initializeDateTime from './initializeDateTime';
import initializeLocales from './initializeLocales';
import markerKeys from './plt/markerKeys';
import owaServiceFeatures from 'owa-service/lib/owaServiceFeatures';
import setUserConfiguration from 'owa-session-store/lib/actions/setUserConfiguration';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { InitializationStage, unblockInitQueue } from 'owa-delayed-initialization';
import { registerRoute, RouterOptions } from 'owa-router';
import {
    doAuthRedirect,
    doHipExceptionRedirect,
    checkAndHandleOwaHipError,
} from 'owa-service-error';
import { getHostLocation, setHostLocation } from 'owa-url/lib/hostLocation';
import { getScopedPath, getExplicitPath } from 'owa-url';
import { AnalyticsOptions, lazyInitializeAnalytics, lazyLogPLTDatapoint } from 'owa-analytics';
import { initializeFeatureFlags, isFeatureEnabled } from 'owa-feature-flags';
import { initializeServiceRequest } from 'owa-service/lib/config';
import { setUseJsonNetSerializer } from 'owa-service/lib/config';
import initializeLicenseNotice from './initializeLicenseNotice';
import { markFunction } from 'owa-performance';
import { OwaWorkload, setOwaWorkload } from 'owa-workloads';
import { stashSessionDataResponses } from 'owa-session-store/lib/services/sessionDataResponseStash';
import { trace } from 'owa-trace';
import { registerFontSubsets, IconFontRegistration } from 'owa-icons';
import { registerDefaultFontFaces } from '@uifabric/styling';
import { getAccessTokenforResourceAsLazy } from 'owa-tokenprovider';
import { isUserIdle } from 'owa-useractivity-manager';
import { lazyinitializeDefaultDiagnostics } from 'owa-diagnostics';
import { PreBootOptions } from 'owa-config';
import { getApp, getPackageBaseUrl, getOwsPath, getOwsPrimePath } from 'owa-config';
import { Middleware } from 'satcheljs';
import { LegacyMiddleware } from 'satcheljs/lib/legacy';

const DelayAfterRenderInMilliseconds = 100;

export interface BootstrapOptions {
    initializeState: () => void;
    renderMainComponent: () => JSX.Element;
    initialConfig: PreBootOptions;
    analyticsOptions?: AnalyticsOptions;
    registerRoutes?: (registerRouteFunction: typeof registerRoute) => void;
    routerOptions?: RouterOptions;
    location?: Location;
    getAuthToken?: () => string;
    getWindowTitle: () => string;
    workload: OwaWorkload;
    iconFonts?: IconFontRegistration[];
    initializeLocalization: (culture: string, bootStrings: Record<string, string>) => Promise<void>;
    satchelMiddleware?: Middleware[];
    satchelLegacyMiddleware?: LegacyMiddleware[];
    /**
     * Component to render when the app-wide error boundary catches an error
     */
    fullErrorComponent: React.ComponentType<{ error: Error }>;
    /**
     * Set of promises that must resolve before starting initial render
     */
    prerenderPromises?: Promise<unknown>[];
}

let isErrorAlreadyHandled: boolean;

function handleDataError(redirectFn: (location: Location) => void, message?: string) {
    if (isErrorAlreadyHandled) {
        return;
    }

    isErrorAlreadyHandled = true;

    try {
        trace.warn(message);
    } finally {
        redirectFn(getHostLocation());
    }
}

/**
 *  Bootstraps the application
 *  See [README.md](../../../../README.md) for details on boot flow
 */
export default markFunction(bootstrap, markerKeys.bootStrap);

function bootstrap(options: BootstrapOptions): Promise<any> {
    setHostLocation(options.location);
    registerDefaultFontFaces(`${getPackageBaseUrl()}resources`);
    setOwaWorkload(options.workload || OwaWorkload.None);

    initializeSatchelMiddleware(options.satchelMiddleware, options.satchelLegacyMiddleware);
    initializeFocusOutline();
    registerFontSubsets(getPackageBaseUrl(), options.iconFonts);
    let windowUrlPath = window && window.location ? window.location.pathname : null;
    let featuresInitialized = false;

    initializeServiceRequest({
        baseUrl: getScopedPath(getOwsPath(), windowUrlPath),
        primeUrl: getExplicitPath(getOwsPrimePath(), windowUrlPath),
        getAuthToken: options.getAuthToken,
        getAuthTokenAsync: getAccessTokenforResourceAsLazy,
        isServer: false,
        onAuthNeeded: () => {
            handleDataError(doAuthRedirect, 'Auth redirect needed.');
        },
        onHipChallengeNeeded: (responseHeaders: Headers): boolean => {
            if (!isFeatureEnabled('fwk-hipExceptionRedirect')) {
                return false;
            }

            return checkAndHandleOwaHipError(
                doHipExceptionRedirect,
                responseHeaders,
                'Hip redirect needed.'
            );
        },
        appName: getApp(),
        isFeatureEnabled: (feature: keyof typeof owaServiceFeatures) => {
            return featuresInitialized && isFeatureEnabled(feature);
        },
        isUserIdle: isUserIdle,
    });

    unblockInitQueue(InitializationStage.Bootstrap);

    let sessionDataPromise = options.initialConfig.sessionDataPromise;
    let userBootSettingsPromise = options.initialConfig.userBootSettingsPromise.then(settings => {
        if (settings.cdnSettings) {
            try {
                settings.cdnSettings.ExtraSettings = JSON.parse(settings.cdnSettings
                    .ExtraSettings as string);
            } catch {
                settings.cdnSettings.ExtraSettings = {};
            }
        }
        return settings;
    });
    let bootStringsPromise = options.initialConfig.bootStringsPromise;

    let renderPromise = Promise.all([
        sessionDataPromise,
        userBootSettingsPromise,
        bootStringsPromise,
    ])
        .then(async data => {
            const [sessionData, userBootSettings, bootStrings] = data;
            const preRenderPromises: Promise<unknown>[] = [...(options.prerenderPromises || [])];
            let featureOverrides: string[] =
                (userBootSettings &&
                    userBootSettings.cdnSettings &&
                    userBootSettings.cdnSettings.ExtraSettings &&
                    userBootSettings.cdnSettings.ExtraSettings.featureOverrides) ||
                [];
            let featureFlags = [...userBootSettings.features, ...featureOverrides];
            setUserConfiguration(sessionData.owaUserConfig);
            sessionData.owaUserConfig = getUserConfiguration();
            stashSessionDataResponses(sessionData, getUserConfiguration());

            preRenderPromises.push(
                initializeLocales(bootStrings, options.initializeLocalization).then(() => {
                    initializeWindowTitle(options.getWindowTitle);
                })
            );

            initializeFeatureFlags(options.location, featureFlags);

            featuresInitialized = true;

            lazyInitializeAnalytics(options.analyticsOptions, featureFlags);

            if (isFeatureEnabled('fwk-devTools')) {
                initializeTracer();
                lazyinitializeDefaultDiagnostics.import().then(init => {
                    init();
                });
            }

            // this should be removed once the index page has shipped 100%
            setErrorHandler(trace.error);

            if (isFeatureEnabled('fwk-useJsonNetSerializer')) {
                setUseJsonNetSerializer(true);
            }

            initializeDateTime(getUserConfiguration());

            initializeLicenseNotice();

            unblockInitQueue(InitializationStage.SessionData);

            // Start the application
            options.initializeState();

            // Router initialization depends on user configuration, so initialize it after
            initializeRouting(options.registerRoutes, options.routerOptions);

            // Await any additional promises before final render

            await Promise.all(preRenderPromises);

            // Return a Promise to render the initial app state
            let asyncRenderPromise = markFunction(asyncRender, markerKeys.render)(
                <ErrorBoundary
                    child={options.renderMainComponent()}
                    fullErrorComponent={options.fullErrorComponent}
                />,
                document.getElementById('app')
            );

            return asyncRenderPromise;
        })
        .then(() => {
            var loadingScreen = document.getElementById('loadingScreen');
            if (loadingScreen) {
                document.body.removeChild(loadingScreen);
            }

            // this should be removed when we remove preboot
            const onBootComplete = (window as any).onBootComplete;
            onBootComplete && onBootComplete();

            setTimeout(() => {
                lazyLogPLTDatapoint.importAndExecute();
                unblockInitQueue(InitializationStage.InitialRender);
            }, DelayAfterRenderInMilliseconds);
        });

    return renderPromise;
}
